import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getTreatments, createTreatment, updateTreatment, deleteTreatment } from '@/lib/api/treatments';
import { logActivity as logActivityDb } from '@/lib/api/activity';
import { refreshStore } from '@/lib/store';
import type { Treatment as DbTreatment } from '@/types/db';

interface Treatment {
  id: string;
  category: string;
  name: string;
  description: string;
  priceAL: string;
  priceDE: string;
  priceAT: string;
  priceCH: string;
  savingsPercent: number;
}

const NEW_ID = '__new__';

function toVM(t: DbTreatment): Treatment {
  return {
    id: t.id,
    category: t.category,
    name: t.name,
    description: t.description ?? '',
    priceAL: t.price_al ?? '',
    priceDE: t.price_de ?? '',
    priceAT: t.price_at ?? '',
    priceCH: t.price_ch ?? '',
    savingsPercent: t.savings_percent ?? 0,
  };
}

function toDb(t: Treatment): Partial<DbTreatment> {
  return {
    category: t.category as DbTreatment['category'],
    name: t.name,
    description: t.description,
    price_al: t.priceAL,
    price_de: t.priceDE,
    price_at: t.priceAT,
    price_ch: t.priceCH,
    savings_percent: t.savingsPercent,
  };
}

const CATEGORIES = [
  { value: 'hair', labelKey: 'admin.service_hair', icon: 'ri-user-heart-line' },
  { value: 'dental', labelKey: 'admin.service_dental', icon: 'ri-tooth-line' },
  { value: 'surgery', labelKey: 'adm1.service_surgery', icon: 'ri-surgical-mask-line' },
];

const DEFAULT_TREATMENTS: Treatment[] = [
  { id: 'tr-1', category: 'hair', name: 'FUE Haartransplantation (bis 5.000 Grafts)', description: 'Moderne FUE-Technik mit Sapphire-Klingen. Bis zu 5.000 Grafts in einer Sitzung. Inklusive PRP und Nachsorge.', priceAL: '1.500 – 2.000', priceDE: '6.500', priceAT: '6.200', priceCH: '7.800', savingsPercent: 77 },
  { id: 'tr-2', category: 'dental', name: 'Zahnimplantat', description: 'Komplettes Zahnimplantat inkl. Krone aus Zirkon. Modernste Implantologie.', priceAL: '350 – 400', priceDE: '1.200 – 1.800', priceAT: '1.200', priceCH: '1.800', savingsPercent: 71 },
  { id: 'tr-3', category: 'dental', name: 'Zirkonkrone', description: 'Hochästhetische Vollkeramik-Krone aus Zirkon. Langlebig und natürlich.', priceAL: '170 – 220', priceDE: '800', priceAT: '750', priceCH: '900', savingsPercent: 79 },
  { id: 'tr-4', category: 'dental', name: 'Keramikkrone', description: 'Hochwertige Keramikkrone mit natürlichem Look. Perfekt auf Ihre Zahnfarbe abgestimmt.', priceAL: '140', priceDE: '600', priceAT: '550', priceCH: '700', savingsPercent: 77 },
  { id: 'tr-5', category: 'dental', name: 'Unsichtbare Zahnspange', description: 'Transparente Aligner für eine diskrete Zahnkorrektur. Moderne Kieferorthopädie.', priceAL: '2.000 – 2.500', priceDE: '4.000', priceAT: '3.800', priceCH: '4.500', savingsPercent: 50 },
  { id: 'tr-6', category: 'dental', name: 'Professionelle Zahnreinigung', description: 'Gründliche professionelle Zahnreinigung für gesunde Zähne und Zahnfleisch.', priceAL: '50', priceDE: '150', priceAT: '140', priceCH: '180', savingsPercent: 67 },
  { id: 'tr-7', category: 'dental', name: 'Zahnaufhellung', description: 'Professionelle Zahnaufhellung für ein strahlendes Lächeln. Sichere und effektive Behandlung.', priceAL: '120 – 150', priceDE: '400', priceAT: '380', priceCH: '450', savingsPercent: 70 },
  { id: 'tr-8', category: 'dental', name: 'Kleiner Knochenaufbau', description: 'Knochenaufbau für eine sichere Implantat-Verankerung bei geringem Knochenabbau.', priceAL: '250', priceDE: '800', priceAT: '750', priceCH: '900', savingsPercent: 69 },
  { id: 'tr-9', category: 'dental', name: 'Großer Knochenaufbau', description: 'Umfassender Knochenaufbau für komplexe Implantatfälle mit erheblichem Knochenabbau.', priceAL: '550', priceDE: '1.200', priceAT: '1.100', priceCH: '1.400', savingsPercent: 54 },
  { id: 'tr-10', category: 'dental', name: 'Sinus Lift', description: 'Sinus-Lift-Operation zur Erhöhung des Kieferknochens im Oberkiefer-Bereich.', priceAL: '400', priceDE: '1.000', priceAT: '950', priceCH: '1.200', savingsPercent: 60 },
  { id: 'tr-11', category: 'dental', name: 'Pterygoid-Implantat', description: 'Spezialisiertes Pterygoid-Implantat für komplexe Fälle mit reduziertem Knochenvolumen.', priceAL: '850', priceDE: '2.000', priceAT: '1.900', priceCH: '2.400', savingsPercent: 58 },
  { id: 'tr-12', category: 'dental', name: 'Zygomatisches Implantat', description: 'Zygomatisches Implantat für Patienten mit starkem Knochenabbau im Oberkiefer.', priceAL: '1.000 – 1.200', priceDE: '2.500', priceAT: '2.400', priceCH: '3.000', savingsPercent: 60 },
  { id: 'tr-13', category: 'dental', name: 'Elastische Prothese', description: 'Flexible und komfortable prothetische Versorgung für mehr Lebensqualität.', priceAL: '1.000', priceDE: '2.500', priceAT: '2.400', priceCH: '3.000', savingsPercent: 60 },
  { id: 'tr-14', category: 'surgery', name: 'Bauchdeckenstraffung', description: 'Straffung der Bauchwand für eine definierte Silhouette. Inklusive Übernachtung im Klinikum.', priceAL: '4.500', priceDE: '8.000', priceAT: '7.500', priceCH: '9.500', savingsPercent: 44 },
  { id: 'tr-15', category: 'surgery', name: 'Nasenkorrektur', description: 'Ästhetische Korrektur der Nasenform für ein harmonisches Gesichtsprofil.', priceAL: '2.200', priceDE: '6.000 – 8.000', priceAT: '5.500', priceCH: '7.000', savingsPercent: 63 },
  { id: 'tr-16', category: 'surgery', name: 'Nasenkorrektur + Septumkorrektur', description: 'Kombinierte ästhetische und funktionelle Nasenkorrektur für bessere Atmung und Ästhetik.', priceAL: '2.500', priceDE: '7.000', priceAT: '6.500', priceCH: '8.000', savingsPercent: 64 },
  { id: 'tr-17', category: 'surgery', name: 'Nasenkorrektur Revision', description: 'Korrektur nach vorheriger Nasen-OP. Spezialisierte Technik für optimale Ergebnisse.', priceAL: '2.800', priceDE: '7.500', priceAT: '7.000', priceCH: '9.000', savingsPercent: 63 },
  { id: 'tr-18', category: 'surgery', name: 'Ohrkorrektur', description: 'Korrektur der Ohrform für ein natürliches Erscheinungsbild. Minimalinvasive Technik.', priceAL: '1.600', priceDE: '4.000', priceAT: '3.800', priceCH: '4.500', savingsPercent: 60 },
  { id: 'tr-19', category: 'surgery', name: 'Unterlidstraffung', description: 'Straffung der Unterlider für ein frisches, jugendliches Erscheinungsbild.', priceAL: '1.800', priceDE: '5.000', priceAT: '4.800', priceCH: '6.000', savingsPercent: 64 },
  { id: 'tr-20', category: 'surgery', name: 'Oberlidstraffung', description: 'Straffung der Oberlider für mehr Frische im Blick. Funktionsverbesserung bei Hängelidern.', priceAL: '1.600', priceDE: '4.500', priceAT: '4.200', priceCH: '5.500', savingsPercent: 64 },
  { id: 'tr-21', category: 'surgery', name: 'Komplette Lidstraffung', description: 'Kombinierte Ober- und Unterlidstraffung für ein komplett erfrischtes Augenpartie.', priceAL: '2.600', priceDE: '7.000', priceAT: '6.500', priceCH: '8.500', savingsPercent: 63 },
  { id: 'tr-22', category: 'surgery', name: 'Brustvergrößerung (Mentor)', description: 'Natürliche Brustvergrößerung mit hochwertigen Mentor-Implantaten. Individuelle Beratung.', priceAL: '4.200', priceDE: '8.000 – 10.000', priceAT: '7.500', priceCH: '9.500', savingsPercent: 48 },
  { id: 'tr-23', category: 'surgery', name: 'Brustverkleinerung', description: 'Brustverkleinerung für mehr Komfort und ein harmonisches Körpergefühl. Inklusive Straffung.', priceAL: '5.000 – 6.500', priceDE: '9.000', priceAT: '8.500', priceCH: '11.000', savingsPercent: 44 },
  { id: 'tr-24', category: 'surgery', name: 'Facelifting', description: 'Gesichtsstraffung für ein jugendliches, natürliches Erscheinungsbild. Moderne OP-Technik.', priceAL: '4.200', priceDE: '12.000 – 15.000', priceAT: '11.500', priceCH: '14.000', savingsPercent: 65 },
  { id: 'tr-25', category: 'surgery', name: 'Facelifting (Mentor)', description: 'Premium Facelifting mit erweitertem Technik-Einsatz für maximale, langanhaltende Ergebnisse.', priceAL: '5.000', priceDE: '14.000', priceAT: '13.000', priceCH: '16.000', savingsPercent: 64 },
  { id: 'tr-26', category: 'surgery', name: 'Gynäkomastie', description: 'Korrektur männlicher Brustdrüsenvergrößerung für ein maskulines Brustbild.', priceAL: '3.500', priceDE: '7.000', priceAT: '6.500', priceCH: '8.500', savingsPercent: 50 },
  { id: 'tr-27', category: 'surgery', name: 'Bauchdeckenstraffung + Liposuktion', description: 'Kombinierte Bauchdeckenstraffung mit Liposuktion für maximale Körperkonturierung.', priceAL: '5.000', priceDE: '10.000', priceAT: '9.500', priceCH: '12.000', savingsPercent: 50 },
  { id: 'tr-28', category: 'surgery', name: 'Liposuktion + BBL', description: 'Kombination aus Liposuktion und Brazilian Butt Lift für eine definierte Silhouette.', priceAL: '5.500', priceDE: '11.000', priceAT: '10.500', priceCH: '13.000', savingsPercent: 50 },
  { id: 'tr-29', category: 'surgery', name: 'Liposuktion (1 Zone)', description: 'Gezielte Fettabsaugung an einer Körperzone für definierte Konturen.', priceAL: '2.500', priceDE: '6.000', priceAT: '5.500', priceCH: '7.000', savingsPercent: 58 },
  { id: 'tr-30', category: 'surgery', name: 'Liposuktion (Total Body)', description: 'Umfassende Fettabsaugung für eine harmonische Gesamtkonturierung des Körpers.', priceAL: '5.000', priceDE: '10.000 – 15.000', priceAT: '9.500', priceCH: '12.000', savingsPercent: 50 },
  { id: 'tr-31', category: 'surgery', name: 'Gesichtslifting', description: 'Umfassendes Gesichtslifting für maximale Verjüngung. Premium-Technik und Nachsorge.', priceAL: '6.500', priceDE: '14.000', priceAT: '13.000', priceCH: '16.000', savingsPercent: 54 },
  { id: 'tr-32', category: 'surgery', name: 'Oberarmstraffung', description: 'Straffung der Oberarme für einen festen, definierten Look. Ideal nach Gewichtsabnahme.', priceAL: '4.000 – 5.000', priceDE: '9.000', priceAT: '8.500', priceCH: '11.000', savingsPercent: 56 },
  { id: 'tr-33', category: 'surgery', name: 'Oberschenkelstraffung', description: 'Straffung der Oberschenkel für eine harmonische Beinform. Inklusive Liposuktion auf Wunsch.', priceAL: '5.500 – 6.000', priceDE: '10.000', priceAT: '9.500', priceCH: '12.000', savingsPercent: 45 },
  { id: 'tr-34', category: 'surgery', name: 'Magenballon', description: 'Nicht-chirurgische Gewichtsreduktion mit Magenballon. Inklusive Betreuung und Entfernung.', priceAL: '2.500 – 3.000', priceDE: '6.000', priceAT: '5.500', priceCH: '7.000', savingsPercent: 58 },
];

export default function TreatmentsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const catLabel = (key: string) => t(key);
  const [treatments, setTreatments] = useState<Treatment[]>([]);
  const [editing, setEditing] = useState<Treatment | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [savedMsg, setSavedMsg] = useState('');

  useEffect(() => {
    if (sessionStorage.getItem('admin_logged_in') !== 'true') {
      navigate('/admin');
      return;
    }
    loadTreatments();
  }, [navigate]);

  const loadTreatments = async () => {
    try {
      const data = await getTreatments();
      setTreatments(data.map(toVM));
    } catch {
      setTreatments([]);
    }
  };

  const afterWrite = async (detail: string) => {
    await logActivityDb('treatments_updated', detail);
    await refreshStore();
    await loadTreatments();
    setSavedMsg(t('adm1.saved'));
    setTimeout(() => setSavedMsg(''), 2000);
  };

  const handleAddNew = () => {
    setEditing({
      id: NEW_ID,
      category: 'hair',
      name: '',
      description: '',
      priceAL: '',
      priceDE: '',
      priceAT: '',
      priceCH: '',
      savingsPercent: 0,
    });
    setShowModal(true);
  };

  const handleEdit = (t: Treatment) => {
    setEditing({ ...t });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!editing) return;
    try {
      if (editing.id === NEW_ID) {
        await createTreatment(toDb(editing));
      } else {
        await updateTreatment(editing.id, toDb(editing));
      }
      await afterWrite('Behandlung gespeichert');
    } catch {
      setSavedMsg(t('adm1.save_error'));
    }
    setShowModal(false);
    setEditing(null);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteTreatment(id);
      await afterWrite('Behandlung gelöscht');
    } catch { /* ignore */ }
    setDeleteConfirm(null);
  };

  const handleImportDefaults = async () => {
    setSavedMsg(t('adm1.importing'));
    try {
      for (let i = 0; i < DEFAULT_TREATMENTS.length; i++) {
        const tr = DEFAULT_TREATMENTS[i];
        await createTreatment({ ...toDb(tr), sort_order: i + 1 });
      }
      await afterWrite('Standardbehandlungen importiert');
    } catch {
      setSavedMsg(t('adm1.import_error'));
    }
  };

  const filtered = filterCategory === 'all'
    ? treatments
    : treatments.filter((t) => t.category === filterCategory);

  if (sessionStorage.getItem('admin_logged_in') !== 'true') return null;

  return (
    <div className="px-4 py-6 md:px-6 space-y-6">
      {/* ── Header ─────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl font-bold text-foreground-900">{t('adm1.treatments_title')}</h1>
          <p className="mt-0.5 text-xs text-foreground-500 font-body">
            {t('adm1.treatments_summary', { count: treatments.length })}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          {savedMsg && <span className="text-xs text-green-600 font-label">{savedMsg}</span>}
          {treatments.length === 0 && (
            <button
              onClick={handleImportDefaults}
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-primary-300 text-primary-600 text-xs font-label hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-download-2-line"></i>
              {t('adm1.treatments_import')}
            </button>
          )}
          <button
            onClick={handleAddNew}
            className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-primary-500 text-white text-xs font-label hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            {t('adm1.treatments_new')}
          </button>
        </div>
      </div>

      {/* ── Category Filter ────────────────────────── */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setFilterCategory('all')}
          className={`px-3 py-2 rounded-full text-xs font-label transition-all cursor-pointer whitespace-nowrap ${
            filterCategory === 'all'
              ? 'bg-foreground-900 text-white'
              : 'bg-background-50 border border-background-200/70 text-foreground-500 hover:text-foreground-700'
          }`}
        >
          {t('adm1.filter_all')}
        </button>
        {CATEGORIES.map((cat) => (
          <button
            key={cat.value}
            onClick={() => setFilterCategory(cat.value)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-label transition-all cursor-pointer whitespace-nowrap ${
              filterCategory === cat.value
                ? 'bg-accent-500 text-white'
                : 'bg-background-50 border border-background-200/70 text-foreground-500 hover:text-foreground-700'
            }`}
          >
            <i className={`${cat.icon} text-xs`}></i>
            {catLabel(cat.labelKey)}
          </button>
        ))}
      </div>

      {/* ── Treatments Table ───────────────────────── */}
      <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-background-100">
                <th className="text-left px-5 py-3 text-[10px] font-label uppercase tracking-[0.1em] text-foreground-400">{t('adm1.col_treatment')}</th>
                <th className="text-left px-5 py-3 text-[10px] font-label uppercase tracking-[0.1em] text-foreground-400">{t('adm1.col_category')}</th>
                <th className="text-right px-5 py-3 text-[10px] font-label uppercase tracking-[0.1em] text-foreground-400">{t('adm1.col_price_al')}</th>
                <th className="text-right px-5 py-3 text-[10px] font-label uppercase tracking-[0.1em] text-foreground-400">{t('adm1.col_price_de')}</th>
                <th className="text-right px-5 py-3 text-[10px] font-label uppercase tracking-[0.1em] text-foreground-400">{t('adm1.col_price_at')}</th>
                <th className="text-right px-5 py-3 text-[10px] font-label uppercase tracking-[0.1em] text-foreground-400">{t('adm1.col_price_ch')}</th>
                <th className="text-center px-5 py-3 text-[10px] font-label uppercase tracking-[0.1em] text-foreground-400">{t('adm1.col_savings')}</th>
                <th className="text-right px-5 py-3 text-[10px] font-label uppercase tracking-[0.1em] text-foreground-400"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item) => {
                const cat = CATEGORIES.find((c) => c.value === item.category);
                return (
                  <tr key={item.id} className="border-b border-background-100 hover:bg-background-50/50 transition-colors">
                    <td className="px-5 py-3">
                      <p className="text-sm font-label font-semibold text-foreground-900">{item.name}</p>
                      <p className="text-[11px] text-foreground-500 font-body truncate max-w-[250px]">{item.description}</p>
                    </td>
                    <td className="px-5 py-3">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-[10px] font-label">
                        <i className={`${cat?.icon || 'ri-price-tag-3-line'} text-[10px]`}></i>
                        {cat ? catLabel(cat.labelKey) : item.category}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right text-sm font-label font-semibold text-foreground-900">{item.priceAL} €</td>
                    <td className="px-5 py-3 text-right text-xs font-body text-foreground-500">{item.priceDE} €</td>
                    <td className="px-5 py-3 text-right text-xs font-body text-foreground-500">{item.priceAT} €</td>
                    <td className="px-5 py-3 text-right text-xs font-body text-foreground-500">{item.priceCH} €</td>
                    <td className="px-5 py-3 text-center">
                      <span className="inline-flex px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[10px] font-label">
                        -{item.savingsPercent}%
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleEdit(item)}
                          className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 text-foreground-400 hover:text-primary-600 transition-colors cursor-pointer"
                          title={t('adm1.action_edit')}
                        >
                          <i className="ri-edit-line text-xs"></i>
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(item.id)}
                          className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-red-50 text-foreground-400 hover:text-red-500 transition-colors cursor-pointer"
                          title={t('adm1.action_delete')}
                        >
                          <i className="ri-delete-bin-line text-xs"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-5 py-12 text-center">
                    <i className="ri-price-tag-3-line text-2xl text-foreground-300"></i>
                    <p className="mt-2 text-xs text-foreground-400 font-body">{t('adm1.treatments_empty')}</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Edit/Add Modal ──────────────────────────── */}
      {showModal && editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowModal(false)}>
          <div
            className="bg-background-50 rounded-2xl border border-background-200/70 w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-5 border-b border-background-100 flex items-center justify-between">
              <h2 className="font-label text-sm font-semibold text-foreground-900">
                {editing.name ? t('adm1.treatments_edit') : t('adm1.treatments_new')}
              </h2>
              <button
                onClick={() => { setShowModal(false); setEditing(null); }}
                className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 text-foreground-400 transition-colors cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Category */}
              <div>
                <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5">{t('adm1.col_category')}</label>
                <select
                  value={editing.category}
                  onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all cursor-pointer"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{catLabel(c.labelKey)}</option>
                  ))}
                </select>
              </div>

              {/* Name */}
              <div>
                <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5">{t('adm1.label_name')}</label>
                <input
                  type="text"
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  placeholder={t('adm1.ph_treatment_name')}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5">{t('adm1.label_description')}</label>
                <textarea
                  value={editing.description}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  placeholder={t('adm1.ph_description')}
                  rows={2}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all resize-y"
                />
              </div>

              {/* Prices grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: t('adm1.label_price_al'), key: 'priceAL' as const },
                  { label: t('adm1.label_price_de'), key: 'priceDE' as const },
                  { label: t('adm1.label_price_at'), key: 'priceAT' as const },
                  { label: t('adm1.label_price_ch'), key: 'priceCH' as const },
                ].map(({ label, key }) => (
                  <div key={key}>
                    <label className="block text-[10px] font-label font-semibold text-foreground-500 mb-1">{label}</label>
                    <input
                      type="text"
                      value={editing[key]}
                      onChange={(e) => setEditing({ ...editing, [key]: e.target.value })}
                      placeholder={t('adm1.ph_price_range')}
                      className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all"
                    />
                  </div>
                ))}
              </div>

              {/* Savings */}
              <div className="w-full sm:w-48">
                <label className="block text-xs font-label font-semibold text-foreground-700 mb-1.5">{t('adm1.label_savings_percent')}</label>
                <input
                  type="number"
                  value={editing.savingsPercent}
                  onChange={(e) => setEditing({ ...editing, savingsPercent: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 font-body focus:outline-none focus:border-primary-400 transition-all"
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-background-100 flex items-center justify-end gap-3">
              <button
                onClick={() => { setShowModal(false); setEditing(null); }}
                className="px-4 py-2 rounded-lg text-xs font-label text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('adm1.cancel')}
              </button>
              <button
                onClick={handleSave}
                className="px-5 py-2 rounded-lg bg-primary-500 text-white text-xs font-label hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('adm1.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm Modal ────────────────────── */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setDeleteConfirm(null)}>
          <div
            className="bg-background-50 rounded-2xl border border-background-200/70 w-full max-w-sm mx-4 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-100 text-red-600 mx-auto mb-3">
              <i className="ri-error-warning-line text-lg"></i>
            </div>
            <h3 className="text-center font-label text-sm font-semibold text-foreground-900">{t('adm1.treatments_delete_confirm')}</h3>
            <p className="mt-1 text-center text-xs text-foreground-500 font-body">
              {t('adm1.delete_irreversible')}
            </p>
            <div className="flex items-center gap-3 mt-5">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2 rounded-lg border border-background-200 text-xs font-label text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('adm1.cancel')}
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 px-4 py-2 rounded-lg bg-red-500 text-white text-xs font-label hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                {t('adm1.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}